/**
 * Modèle de l'explorateur du Business Graph (ADR-129), sans React : la réponse d'une traversée
 * (`graph-traversal`, lue sur le fil) devient des couloirs, des groupes, des cartes placées et des liens
 * tracés. Tout est calculé ici, côté client, sur au plus 1 000 nœuds ; rien n'est inventé : un nœud que
 * le serveur n'a pas rendu (refusé, absent) n'existe pas, et n'est compté nulle part (ERP-AUTH-003).
 */

export interface WireRef {
  type: string;
  id: string;
}

export interface WireNode {
  ref: WireRef;
  depth: number;
  label?: string;
}

export interface WireEdge {
  relation_id: string;
  semantic_kind?: string;
  source: WireRef;
  target: WireRef;
  attributes?: Record<string, unknown>;
  edge_id?: string;
  certainty?: string;
  /** Lien écrit par une correction approuvée (graph-traversal 1.2.0) : seul il se clôt à la main. */
  provenance?: 'correction';
  /** Instant du fait qui a écrit le lien (graph-traversal 1.2.0). */
  valid_from?: string;
}

/** Réponse d'une traversée sur le fil (contrat `graph-traversal` 1.1.0) : identifiants et dates en chaînes. */
export interface WireTraversal {
  start: WireRef;
  direction: 'upstream' | 'downstream' | 'both';
  source: 'authoritative' | 'projection';
  at?: string;
  as_of?: string;
  nodes: WireNode[];
  edges: WireEdge[];
  truncated: boolean;
}

/** Au-delà, les voisins d'une même relation se replient en un nœud (ADR-129 §3, seuil retenu le 2026-10-15). */
export const COLLAPSE_THRESHOLD = 60;

export const keyOf = (ref: WireRef): string => `${ref.type}:${ref.id}`;

/** Natures qui portent un flux (amont, aval) ; les autres sont des références, au-dessus ou en dessous. */
const FLOW_KINDS = new Set(['composition', 'transformation', 'provenance_link']);

export type CertaintyStyle = 'exact' | 'batch' | 'estimated';

/**
 * Style d'un lien selon sa certitude (ADR-107 §4) : plein pour l'exact (et le déterminé par règle), tirets
 * pour ce qui est rattaché par lot ou par bilan, pointillé pour l'estimé et l'inconnu. Absente = exacte.
 */
export function certaintyStyle(certainty: string | undefined): CertaintyStyle {
  if (certainty === undefined || certainty === 'exact' || certainty === 'deterministic') return 'exact';
  if (certainty === 'mass_balance' || certainty === 'temporal_candidate' || certainty === 'inferred') return 'batch';
  return 'estimated';
}

export const CERTAINTY_LABELS: Readonly<Record<string, string>> = {
  exact: 'exacte',
  deterministic: 'déterminée',
  inferred: 'déduite',
  temporal_candidate: 'candidate (dates)',
  mass_balance: 'par lot',
  estimated: 'estimée',
  unknown: 'inconnue',
};

export const CERTAINTY_HINTS: Readonly<Record<string, string>> = {
  exact: 'Lien lu tel qu’il a été enregistré.',
  deterministic: 'Lien établi par une règle déterministe.',
  inferred: 'Lien déduit, pas observé.',
  temporal_candidate: 'Lien candidat, rapproché par les dates.',
  mass_balance: 'Rattaché par son lot ou par bilan matière, pas par une lecture de l’objet.',
  estimated: 'Lien estimé : à confirmer avant toute décision.',
  unknown: 'Certitude inconnue.',
};

export const certaintyLabel = (certainty: string | undefined): string => CERTAINTY_LABELS[certainty ?? 'exact'] ?? certainty ?? 'exacte';

export const STROKE_DASH: Readonly<Record<CertaintyStyle, string | undefined>> = { exact: undefined, batch: '7 5', estimated: '2 5' };

/** Une arête est-elle une équivalence d'identité (un absorbé et son survivant, ADR-074) ? */
const isEquivalence = (edge: WireEdge): boolean => edge.attributes?.['equivalence'] === 'same_as';

export const isFlow = (edge: WireEdge): boolean => !isEquivalence(edge) && edge.semantic_kind !== undefined && FLOW_KINDS.has(edge.semantic_kind);

export type Band = 'above' | 'flow' | 'below';

export interface PlacedNode {
  key: string;
  ref: WireRef;
  label?: string;
  /** Colonne : 0 l'objet exploré, négative en amont, positive en aval. */
  column: number;
  band: Band;
  /** Arête qui l'a atteint depuis l'objet exploré (parcours en largeur) : le chemin à surligner. */
  via?: number;
  parent?: string;
  /** Groupe : même colonne, même bande, même relation, même type. */
  group: string;
}

/**
 * Place chaque nœud en partant de l'objet exploré : un lien de flux mène en amont (la cible d'une arête
 * est en amont de sa source) ou en aval ; une référence reste dans la colonne, au-dessus si l'objet la
 * porte, en dessous s'il est cité. Un nœud injoignable par les arêtes rendues reste en aval de sa profondeur.
 */
export function placeNodes(traversal: WireTraversal): Map<string, PlacedNode> {
  const nodes = new Map<string, WireNode>(traversal.nodes.map((node) => [keyOf(node.ref), node]));
  const startKey = keyOf(traversal.start);
  const placed = new Map<string, PlacedNode>();
  const start = nodes.get(startKey);
  placed.set(startKey, { key: startKey, ref: traversal.start, ...(start?.label === undefined ? {} : { label: start.label }), column: 0, band: 'flow', group: 'center' });
  const incident = new Map<string, number[]>();
  traversal.edges.forEach((edge, index) => {
    for (const end of [keyOf(edge.source), keyOf(edge.target)]) incident.set(end, [...(incident.get(end) ?? []), index]);
  });
  const queue = [startKey];
  while (queue.length > 0) {
    const current = placed.get(queue.shift()!)!;
    for (const index of incident.get(current.key) ?? []) {
      const edge = traversal.edges[index]!;
      const fromSource = keyOf(edge.source) === current.key;
      const otherRef = fromSource ? edge.target : edge.source;
      const otherKey = keyOf(otherRef);
      if (placed.has(otherKey) || !nodes.has(otherKey)) continue;
      let column = current.column;
      let band: Band = current.band;
      if (isFlow(edge)) {
        column = fromSource ? current.column - 1 : current.column + 1;
        band = 'flow';
      } else if (current.column === 0 && current.key === startKey) {
        band = fromSource ? 'above' : 'below';
      }
      const label = nodes.get(otherKey)?.label;
      placed.set(otherKey, { key: otherKey, ref: otherRef, ...(label === undefined ? {} : { label }), column, band, via: index, parent: current.key, group: `${column}|${band}|${edge.relation_id}|${otherRef.type}` });
      queue.push(otherKey);
    }
  }
  for (const node of traversal.nodes) {
    const key = keyOf(node.ref);
    if (placed.has(key)) continue;
    const column = traversal.direction === 'upstream' ? -node.depth : node.depth;
    placed.set(key, { key, ref: node.ref, ...(node.label === undefined ? {} : { label: node.label }), column, band: 'flow', group: `${column}|flow|-|${node.ref.type}` });
  }
  return placed;
}

/** Nœuds et arêtes du chemin qui relie l'objet exploré à un nœud (surligné, le reste atténué). */
export function pathTo(placed: ReadonlyMap<string, PlacedNode>, key: string): { nodes: Set<string>; edges: Set<number> } {
  const nodes = new Set<string>();
  const edges = new Set<number>();
  let cursor = placed.get(key);
  while (cursor !== undefined && !nodes.has(cursor.key)) {
    nodes.add(cursor.key);
    if (cursor.via !== undefined) edges.add(cursor.via);
    cursor = cursor.parent === undefined ? undefined : placed.get(cursor.parent);
  }
  return { nodes, edges };
}

export interface CollapsedGroup {
  group: string;
  members: string[];
}

/**
 * Repli (ADR-129 §3) : tant que plus de `threshold` nœuds sont visibles, le plus grand groupe d'au moins
 * deux nœuds se replie en un seul nœud qui annonce son nombre. Rien n'est retiré : un groupe replié se
 * déplie en liste. L'objet exploré n'est jamais replié.
 */
export function collapseGroups(visible: readonly PlacedNode[], threshold = COLLAPSE_THRESHOLD): Map<string, CollapsedGroup> {
  const groups = new Map<string, string[]>();
  for (const node of visible) if (node.group !== 'center') groups.set(node.group, [...(groups.get(node.group) ?? []), node.key]);
  const collapsed = new Map<string, CollapsedGroup>();
  let count = visible.length;
  while (count > threshold) {
    const candidates = [...groups.entries()].filter(([group, members]) => !collapsed.has(group) && members.length >= 2).sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
    const largest = candidates[0];
    if (largest === undefined) break;
    collapsed.set(largest[0], { group: largest[0], members: largest[1] });
    count -= largest[1].length - 1;
  }
  return collapsed;
}

export const relationOfGroup = (group: string): string => group.split('|')[2] ?? '';
export const typeOfGroup = (group: string): string => group.split('|')[3] ?? '';

/**
 * Quantité portée par une arête (attribut `quantity` du Core, ADR-021) : une valeur de présentation pour la
 * largeur d'un flux, jamais un montant ni une valeur recalculée.
 */
export function quantityOf(edge: WireEdge): { value: number; unit?: string } | null {
  const raw = edge.attributes?.['quantity'];
  const entry = typeof raw === 'object' && raw !== null ? (raw as { value?: unknown; unit?: unknown }) : { value: raw };
  const value = typeof entry.value === 'number' ? entry.value : typeof entry.value === 'string' && /^-?\d+(\.\d+)?$/.test(entry.value.trim()) ? Number.parseFloat(entry.value) : Number.NaN;
  if (!Number.isFinite(value) || value <= 0) return null;
  return typeof entry.unit === 'string' ? { value, unit: entry.unit } : { value };
}

/** Comptes par type des nœuds rendus, objet exploré exclu (en-tête chiffré, filtres par type). */
export function countByType(nodes: readonly { ref: WireRef }[], startKey: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of nodes) if (keyOf(node.ref) !== startKey) counts.set(node.ref.type, (counts.get(node.ref.type) ?? 0) + 1);
  return counts;
}

/** Relation lisible quand aucun libellé n'est déclaré : l'identifiant, jamais un texte inventé. */
export function humanize(relationId: string): string {
  const words = relationId.replace(/_/g, ' ').trim();
  return words === '' ? relationId : words;
}

export type Direction = 'upstream' | 'downstream' | 'reference';

/** Sens d'un lien vu de l'objet exploré : celui de la colonne la plus éloignée de ses deux bouts. */
export function directionOf(placed: ReadonlyMap<string, PlacedNode>, edge: WireEdge): Direction {
  const source = placed.get(keyOf(edge.source));
  const target = placed.get(keyOf(edge.target));
  const far = [source, target].filter((node): node is PlacedNode => node !== undefined).sort((a, b) => Math.abs(b.column) - Math.abs(a.column))[0];
  if (far === undefined || far.column === 0) return 'reference';
  return far.column < 0 ? 'upstream' : 'downstream';
}

export const DIRECTION_LABELS: Readonly<Record<Direction, string>> = { upstream: 'Amont', downstream: 'Aval', reference: 'Référence' };

/** L'extrémité d'une arête la plus éloignée de l'objet exploré : « l'objet » d'une ligne du tableau. */
export function farEnd(placed: ReadonlyMap<string, PlacedNode>, edge: WireEdge): WireRef {
  const source = placed.get(keyOf(edge.source));
  const target = placed.get(keyOf(edge.target));
  if (source === undefined || target === undefined) return edge.target;
  if (Math.abs(source.column) !== Math.abs(target.column)) return Math.abs(source.column) > Math.abs(target.column) ? edge.source : edge.target;
  return target.parent === source.key ? edge.target : edge.source;
}

/** Coins droits du côté où des liens s'attachent à une carte (remarque de l'utilisateur du 2026-10-15). */
export type Side = 'left' | 'right' | 'top' | 'bottom';

export function borderRadius(sides: ReadonlySet<Side>, radius: number): string {
  const corner = (a: Side, b: Side): string => (sides.has(a) || sides.has(b) ? '0' : `${radius}px`);
  return `${corner('top', 'left')} ${corner('top', 'right')} ${corner('bottom', 'right')} ${corner('bottom', 'left')}`;
}

/** Tracé courbe d'un lien entre deux côtés de cartes. */
export function curve(from: { x: number; y: number }, to: { x: number; y: number }, vertical: boolean): string {
  if (vertical) {
    const middle = (from.y + to.y) / 2;
    return `M ${from.x} ${from.y} C ${from.x} ${middle} ${to.x} ${middle} ${to.x} ${to.y}`;
  }
  const middle = (from.x + to.x) / 2;
  return `M ${from.x} ${from.y} C ${middle} ${from.y} ${middle} ${to.y} ${to.x} ${to.y}`;
}
